/**
 * SeriesDirectory — public listing of every published brand across all
 * organizers (`/series`). Helps cross-promotion and discovery.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Users, MapPin, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeriesDirectory() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/brands`);
        if (!cancelled) setBrands(res.data?.brands || []);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="series-directory">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-10">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold uppercase tracking-wider">
            <Sparkles className="h-4 w-4" /> Memberships &amp; Series
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mt-2">
            Find your scene.
          </h1>
          <p className="text-base text-muted-foreground mt-3 max-w-2xl">
            Join a club, grab a multi-event passport, or follow a series. One purchase, every event.
          </p>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
          </div>
        ) : brands.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-10 text-center">
              <h3 className="text-lg font-semibold text-foreground">No series yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Check back soon — organizers are spinning up their first clubs.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="brands-grid">
            {brands.map((b) => (
              <Link
                key={b.brand_id}
                to={`/series/${b.slug}`}
                data-testid={`brand-card-${b.brand_id}`}
                className="group"
              >
                <Card className="bg-card border-border overflow-hidden hover:border-indigo-700/60 transition-colors h-full">
                  <div
                    className="h-32 bg-gradient-to-br from-indigo-900/40 to-purple-900/40 relative"
                    style={b.hero_image_url ? { backgroundImage: `url(${b.hero_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                  >
                    {b.logo_url && (
                      <img
                        src={b.logo_url}
                        alt=""
                        className="absolute bottom-2 left-3 h-10 w-10 rounded-md border border-border bg-background object-cover"
                      />
                    )}
                  </div>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <h3 className="font-bold text-base text-foreground line-clamp-1 group-hover:text-indigo-300 transition-colors">
                        {b.name}
                      </h3>
                    </div>
                    {b.tagline && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{b.tagline}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {b.events_count} event{b.events_count === 1 ? '' : 's'}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {b.members_count} member{b.members_count === 1 ? '' : 's'}
                      </span>
                      {b.city && (
                        <span className="inline-flex items-center gap-1 capitalize">
                          <MapPin className="h-3 w-3" /> {b.city}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
